import { Helmet } from "react-helmet-async";
import { Link, Navigate, useParams } from "react-router-dom";
import { Seo } from "../components/Seo";
import { BlogCTA } from "../components/BlogCTA";
import { FacePortrait } from "../components/FacePortrait";
import { AUTHORS, authorPath } from "../data/authors";
import { GUIDES, articlePath } from "../data/guides";
import { generatePersonSchema } from "../lib/structuredData";

/**
 * Public author profile at /auteur/<slug>. Renders the byline person behind the
 * blog, the articles attributed to them, and ProfilePage/Person JSON-LD for
 * E-E-A-T. Unknown slugs redirect to the blog index.
 */
export function Author() {
  const { slug } = useParams<{ slug: string }>();
  const author = slug ? AUTHORS[slug] : undefined;

  if (!author) {
    return <Navigate to="/blog" replace />;
  }

  const description = `${author.name} — ${author.role}. ${author.bio}`;
  const schema = generatePersonSchema({
    name: author.name,
    jobTitle: author.role,
    description: author.bio,
    path: authorPath(author),
  });

  return (
    <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-16 lg:px-8">
      <Seo title={author.name} description={description} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <Link
        to="/blog"
        className="mb-6 inline-block py-2 text-sm text-slate-500 hover:text-slate-900"
      >
        ← Terug naar blog
      </Link>

      <header className="flex flex-col gap-6 border border-slate-300 bg-white p-6 sm:flex-row sm:items-center sm:gap-8 sm:p-10">
        <FacePortrait name={author.name} size={96} shape="square" />
        <div>
          <span className="inline-block bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">
            {author.role}
          </span>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {author.name}
          </h1>
          <p className="mt-2 text-lg leading-relaxed text-slate-600">
            {author.bio}
          </p>
        </div>
      </header>

      <section className="mt-12" aria-label={`Artikelen van ${author.name}`}>
        <h2 className="mb-6 text-2xl font-bold text-slate-900">
          Artikelen van {author.name}
        </h2>
        <div className="grid gap-6 sm:grid-cols-2">
          {GUIDES.map((post) => (
            <Link
              key={post.slug}
              to={articlePath(post)}
              className="group flex flex-col border-2 border-slate-200 bg-white p-6 transition-all hover:border-accent"
            >
              <div className="mb-3 flex items-center gap-3">
                <span className="inline-block bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                  {post.category}
                </span>
                <span className="text-sm text-slate-500">
                  {post.readingTime} min
                </span>
              </div>
              <h3 className="mb-2 text-lg font-bold leading-tight text-slate-900 transition-colors group-hover:text-accent">
                {post.title}
              </h3>
              <p className="text-sm leading-relaxed text-slate-600">
                {post.excerpt}
              </p>
            </Link>
          ))}
        </div>
      </section>

      <BlogCTA />
    </main>
  );
}
